import React, { useState, useEffect } from 'react';
import { 
  Check, Zap, ShieldCheck, 
  Loader2, History, ExternalLink, Calendar,
  User, Smartphone, CreditCard, X, AlertCircle, Trash2, Star, CheckCircle2, Clock, AlertTriangle, Building2,
  Mail, RefreshCw 
} from 'lucide-react';
import { auth } from '../config/firebase';
import { pagamentoService, usuarioService } from '../services/api';
import { PRECOS, PLANOS_VENDAVEIS } from '../config/planos';

const fmt = (valor) => valor.toFixed(2).replace('.', ',');

// Estilo visual por tema — cada plano vendável em ../config/planos.js aponta pra um destes.
const TEMA_CARD = {
  slate: {
    borda: 'border-2 border-slate-200 hover:border-[#1883FF]/50 hover:shadow-lg',
    titulo: 'text-slate-900',
    check: 'text-[#1883FF]',
    badge: 'bg-slate-800',
    botao: 'bg-slate-900 hover:bg-[#1883FF]',
  },
  purple: {
    borda: 'border-2 border-[#6D28D9]/40 shadow-[0_20px_40px_-15px_rgba(109,40,217,0.2)] hover:-translate-y-1',
    titulo: 'text-[#6D28D9]',
    check: 'text-[#6D28D9]',
    badge: 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9]',
    botao: 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] shadow-lg shadow-[#6D28D9]/20 hover:opacity-90',
  },
  dark: {
    borda: 'border-2 border-slate-800 shadow-[0_20px_40px_-15px_rgba(15,23,42,0.2)] hover:-translate-y-1',
    titulo: 'text-slate-900',
    check: 'text-slate-800',
    badge: 'bg-slate-900',
    botao: 'bg-slate-900 shadow-lg shadow-slate-900/20 hover:opacity-90',
  },
};

const PlanosPage = () => {
  const [loading, setLoading] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ cpfCnpj: '', telefone: '' });
  
  const [statusAssinatura, setStatusAssinatura] = useState('FREE');
  const [planoAtual, setPlanoAtual] = useState('COMUM'); 
  const [planoSelecionado, setPlanoSelecionado] = useState('COMUM'); 
  
  // Estado para controlar o ciclo de pagamento escolhido na tela
  const [cicloSelecionado, setCicloSelecionado] = useState('MENSAL'); 
  
  const [dataValidade, setDataValidade] = useState(null);
  const [isReativando, setIsReativando] = useState(false); 

  const carregarDadosIniciais = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        const usuarioId = user.uid;
        
        const resHist = await pagamentoService.listarHistorico(usuarioId, token);
        setHistorico(resHist.data);

        const resPerfil = await usuarioService.obterPerfil(usuarioId);
        const statusBanco = resPerfil.data.status_assinatura;
        setStatusAssinatura(statusBanco ? statusBanco.toUpperCase().trim() : 'FREE');
        setPlanoAtual(resPerfil.data.plano || 'COMUM');
        setDataValidade(resPerfil.data.data_validade || null);
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoadingHist(false);
    }
  };

  useEffect(() => {
    carregarDadosIniciais();
  }, []); 

  const handleCpfChange = (e) => {
    let val = e.target.value.replace(/\D/g, '');
    if (val.length <= 11) {
      val = val.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    } else {
      val = val.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    setFormData({ ...formData, cpfCnpj: val });
  };

  const handleCancelarAssinatura = async () => {
    if (!window.confirm("Tem certeza que deseja cancelar sua assinatura? Você perderá acesso aos recursos avançados.")) return;

    setLoadingCancel(true);
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();
      await pagamentoService.cancelarAssinatura(user.uid, token);
      alert("Assinatura cancelada com sucesso.");
      window.location.reload(); 
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao cancelar.");
    } finally {
      setLoadingCancel(false);
    }
  };

  const handleAssinar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();
      
      const dadosParaEnviar = {
        usuarioId: user.uid,
        nome: user.displayName || 'Usuário',
        email: user.email,
        cpfCnpj: formData.cpfCnpj.replace(/\D/g, ''),
        telefone: formData.telefone.replace(/\D/g, ''),
        plano: planoSelecionado,
        ciclo: cicloSelecionado // Envia o ciclo para o backend
      };
      
      // Disparo do Pixel da Meta
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          content_name: `${planoSelecionado} - ${cicloSelecionado}`,
          currency: 'BRL',
          value: cicloSelecionado === 'ANUAL' ? PRECOS[planoSelecionado].anualTotal : PRECOS[planoSelecionado].mensal
        });
      }

      const res = await pagamentoService.assinarAgora(dadosParaEnviar, token);
      
      alert(res.data?.message || "Pedido gerado com sucesso! Verifique seu e-mail e SMS para realizar o pagamento.");
      
      setShowModal(false);
      window.location.reload(); 
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao processar assinatura.");
    } finally {
      setLoading(false);
    }
  };

  const openModalPrimeiraAssinatura = (plano) => {
    setPlanoSelecionado(plano);
    setIsReativando(false);
    setShowModal(true);
  };

  const openModalReativacao = (plano) => {
    setPlanoSelecionado(plano);
    setIsReativando(true);
    setShowModal(true);
  };

  const verificarExpiracaoTrial = () => {
    if (!dataValidade) return false;
    const prazo = new Date(dataValidade);
    return new Date() > prazo; 
  };

  const trialExpirou = verificarExpiracaoTrial();
  
  // Flags de Controle
  const isFree = statusAssinatura === 'FREE' || !statusAssinatura;
  const isTrial = statusAssinatura === 'TRIAL' && !trialExpirou; 
  const isAtivo = statusAssinatura === 'ACTIVE';
  const isPendente = statusAssinatura === 'PENDENTE' || statusAssinatura === 'PENDING';
  const isExpirado = statusAssinatura === 'EXPIRED' || statusAssinatura === 'CANCELED' || statusAssinatura === 'INACTIVE' || statusAssinatura === 'OVERDUE' || (statusAssinatura === 'TRIAL' && trialExpirou);

  // Componente Reutilizável de Escolha de Planos
  const RenderGridPlanos = ({ isReativando }) => (
    <div className="w-full max-w-[900px] mx-auto text-left">
      
      {/* TOGGLE MENSAL/ANUAL */}
      <div className="flex justify-center mb-10">
        <div className="bg-slate-100 p-1.5 rounded-full flex items-center gap-1 relative shadow-inner">
          <button
            onClick={() => setCicloSelecionado('MENSAL')}
            className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${cicloSelecionado === 'MENSAL' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Mensal
          </button>
          <button
            onClick={() => setCicloSelecionado('ANUAL')}
            className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${cicloSelecionado === 'ANUAL' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Anual <span className="bg-emerald-400 text-emerald-950 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-black">Desconto</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1200px] mx-auto">
        {PLANOS_VENDAVEIS.map((plano) => {
          const tema = TEMA_CARD[plano.tema];
          const preco = PRECOS[plano.id];
          const mostrarBadge = plano.badgeSempreVisivel || !isReativando;
          return (
            <div key={plano.id} className={`relative bg-white rounded-[2.5rem] p-8 sm:p-10 flex flex-col transition-all ${tema.borda}`}>
              {mostrarBadge && (
                <div className={`absolute -top-4 left-1/2 -translate-x-1/2 text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-wider shadow-md whitespace-nowrap flex items-center gap-1.5 ${tema.badge}`}>
                  {plano.id === 'PREMIUM' && <Star size={12} fill="white" />}
                  {plano.badge}
                </div>
              )}

              <h3 className={`text-2xl font-black uppercase tracking-tight mb-2 flex items-center gap-2 ${tema.titulo}`}>
                {plano.nome} {plano.id === 'EMPRESARIAL' && <Building2 size={24} />}
              </h3>
              <p className="text-xs font-semibold text-slate-500 mb-6 h-8">{plano.descricao}</p>

              <div className="mb-8">
                <div className="text-[48px] font-black text-slate-900 tracking-tighter flex items-baseline">
                  R${cicloSelecionado === 'ANUAL' ? fmt(preco.anualMes) : fmt(preco.mensal)}
                  <span className="text-sm text-[#64748b] font-semibold tracking-normal ml-1">/mês</span>
                </div>
                {cicloSelecionado === 'ANUAL' ? (
                  <p className="text-[11px] font-bold text-emerald-600 mt-1">Economize R$ {preco.economia}! Cobrado R$ {fmt(preco.anualTotal)} anualmente</p>
                ) : (
                  <p className="text-[11px] font-bold text-slate-400 mt-1">Cobrado mensalmente (Apenas Cartão)</p>
                )}
              </div>

              <div className="flex flex-col gap-4 mb-10 flex-1">
                {plano.bullets.map((bullet, i) => (
                  <div key={i} className="flex items-center gap-3.5 text-sm font-bold text-slate-700">
                    <Check size={20} strokeWidth={3} className={`${tema.check} shrink-0`} /> {bullet}
                  </div>
                ))}
              </div>

              <button
                className={`w-full h-14 text-white border-none rounded-2xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-3 cursor-pointer transition-all hover:-translate-y-0.5 shadow-md ${tema.botao}`}
                onClick={() => isReativando ? openModalReativacao(plano.id) : openModalPrimeiraAssinatura(plano.id)}
              >
                {isReativando ? `Reativar ${plano.nome}` : `Começar com ${plano.nome}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-5xl mx-auto p-2 sm:p-4 animate-fade-in-up">
      
      <header className="text-center mb-12">
        <p className="text-[#64748b] text-[11px] sm:text-xs font-black uppercase tracking-widest mb-1.5">Potencialize sua produtividade agora</p>
        <h1 className="text-3xl sm:text-4xl font-black uppercase italic tracking-tight text-slate-900">
          Planos <span className="text-[#1883FF]">Sincro</span>
        </h1>
      </header>

      <div className="flex justify-center mb-16">
        
        {isFree && <RenderGridPlanos isReativando={false} />}

        {isTrial && (
          <div className="relative w-full max-w-[520px] bg-white rounded-[2.5rem] p-8 sm:p-12 border-2 border-amber-500 shadow-[0_25px_50px_rgba(245,158,11,0.12)]">
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-7 py-3 rounded-full text-[11px] font-black uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-amber-500/30 whitespace-nowrap">
              <Clock size={16} color="white" /> Período de Teste Ativo
            </div>
            <div className="text-center my-6">
              <Zap size={60} className="text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 uppercase">MODO DEGUSTAÇÃO</h2>
              <p className="text-xs sm:text-sm text-[#64748b] font-semibold mt-2.5 leading-relaxed">
                Você está testando o <b>Plano {planoAtual}</b> gratuitamente. 
                <strong className="text-slate-900"> Complete sua assinatura</strong> para evitar interrupções ao fim do teste.
              </p>
            </div>
            
            <div className="bg-amber-50 p-5 rounded-2xl border border-amber-100 mb-6">
              <div className="flex items-center gap-3 text-amber-800 text-left">
                <AlertCircle size={20} className="shrink-0" /> 
                <span className="text-xs font-bold">
                  Assine agora e pague apenas após o fim do seu teste{dataValidade && ` (${new Date(dataValidade).toLocaleDateString()})`}.
                </span>
              </div>
            </div>

            <button className="w-full h-14 bg-amber-500 text-white border-none rounded-2xl text-sm font-black uppercase tracking-wider flex items-center justify-center gap-3 cursor-pointer transition-all duration-300 hover:bg-amber-600 hover:-translate-y-0.5 shadow-md" onClick={() => openModalPrimeiraAssinatura(planoAtual)}>
              <CreditCard size={20} /> Finalizar Assinatura ({planoAtual})
            </button>
          </div>
        )}

        {isAtivo && (
          <div className="relative w-full max-w-[520px] bg-white rounded-[2.5rem] p-8 sm:p-12 border-2 border-slate-200 shadow-sm">
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-7 py-3 rounded-full text-[11px] font-black uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-emerald-500/30 whitespace-nowrap">
              <Star size={16} fill="white" /> Assinatura Ativa
            </div>
            <div className="text-center my-6">
              <ShieldCheck size={60} className="text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 uppercase">VOCÊ É {planoAtual}</h2>
              <p className="text-xs sm:text-sm text-[#64748b] font-semibold mt-2.5 leading-relaxed">
                Sua assinatura está ativa e você tem acesso aos recursos do seu plano atual.
              </p>
            </div>
            
            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 mb-6">
              <div className="flex items-center gap-3 text-slate-700 text-left">
                <CheckCircle2 size={20} className="text-emerald-500 shrink-0" /> 
                <span className="text-xs font-bold">Sua assinatura está em dia e funcionando perfeitamente.</span>
              </div>
            </div>

            <button 
              className="w-full h-14 bg-red-50 text-red-500 border-2 border-red-100 rounded-2xl text-sm font-black uppercase tracking-wider flex items-center justify-center gap-3 cursor-pointer transition-all duration-300 hover:bg-red-100 hover:text-red-700 disabled:opacity-60 disabled:cursor-not-allowed" 
              onClick={handleCancelarAssinatura}
              disabled={loadingCancel}
            >
              {loadingCancel ? <Loader2 className="animate-spin" size={20} /> : <Trash2 size={20} />}
              {loadingCancel ? 'Cancelando...' : 'Cancelar Assinatura'}
            </button>
          </div>
        )}

        {isPendente && (
          <div className="relative w-full max-w-[520px] bg-white rounded-[2.5rem] p-8 sm:p-12 border-2 border-[#1883FF] shadow-sm">
            <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#1883FF] text-white px-7 py-3 rounded-full text-[11px] font-black uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-blue-500/30 whitespace-nowrap">
              <Clock size={16} color="white" /> Aguardando Pagamento
            </div>
            
            <div className="text-center my-6">
              <Mail size={60} className="text-[#1883FF] mx-auto mb-4" />
              <h2 className="text-xl sm:text-2xl font-black text-slate-900 uppercase">Fatura Enviada</h2>
              <p className="text-xs sm:text-sm text-[#64748b] font-semibold mt-2.5 leading-relaxed">
                Seu pedido para o <b>Plano {planoAtual}</b> foi gerado. Enviamos o link de pagamento para o seu <b>e-mail e SMS</b> cadastrados.
              </p>
            </div>
            
            <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100 mb-6">
              <div className="flex items-center gap-3 text-blue-800 text-left">
                <AlertCircle size={20} className="shrink-0" /> 
                <span className="text-xs font-bold">
                  Assim que o pagamento for liquidado, seu plano será ativado automaticamente. Isso pode levar alguns minutos dependendo da forma de pagamento.
                </span>
              </div>
            </div>

            <button 
              className="w-full h-14 bg-slate-100 text-slate-600 border-none rounded-2xl text-sm font-black uppercase tracking-wider flex items-center justify-center gap-3 cursor-pointer transition-all duration-300 hover:bg-slate-200 shadow-sm"
              onClick={() => window.location.reload()}
            >
              <RefreshCw size={20} /> Atualizar Status
            </button>
          </div>
        )}

        {isExpirado && (
          <div className="w-full flex flex-col items-center gap-8">
            <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-2xl font-bold flex items-center gap-3">
              <AlertCircle size={20} />
              Seu acesso {planoAtual === 'TRIAL' ? 'de teste' : ''} expirou ou foi interrompido. Escolha um plano abaixo para reativar os serviços.
            </div>
            <RenderGridPlanos isReativando={true} />
          </div>
        )}

      </div>

      <div className="bg-white p-6 sm:p-10 rounded-[2.5rem] border border-slate-100 shadow-sm text-left">
        <h3 className="text-base sm:text-lg font-black uppercase italic tracking-wider mb-8 flex items-center gap-3.5">
          <History size={24} className="text-[#1883FF]" /> Histórico Financeiro
        </h3>
        
        {loadingHist ? (
          <div className="text-center p-8"><Loader2 className="animate-spin text-[#1883FF] mx-auto" size={32} /></div>
        ) : historico.length === 0 ? (
          <p className="text-sm font-bold text-[#94a3b8] italic m-0">Nenhum pagamento ou fatura registrada.</p>
        ) : (
          <div className="w-full overflow-x-auto rounded-xl">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-100">
                  <th className="text-left p-4 text-[10px] sm:text-[11px] font-black text-[#64748b] uppercase tracking-wider">Data de Emissão</th>
                  <th className="text-left p-4 text-[10px] sm:text-[11px] font-black text-[#64748b] uppercase tracking-wider">Valor Total</th>
                  <th className="text-left p-4 text-[10px] sm:text-[11px] font-black text-[#64748b] uppercase tracking-wider">Status</th>
                  <th className="text-left p-4 text-[10px] sm:text-[11px] font-black text-[#64748b] uppercase tracking-wider">Documento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {historico.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-5 text-sm font-bold text-slate-800 flex items-center gap-2 whitespace-nowrap">
                      <Calendar size={16} className="text-[#64748b]" /> {new Date(item.data_pagamento).toLocaleDateString()}
                    </td>
                    <td className="p-5 text-sm font-black text-slate-900 whitespace-nowrap">R$ {item.valor}</td>
                    <td className="p-5 whitespace-nowrap">
                      <span className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wide ${item.status === 'CONFIRMED' || item.status === 'RECEIVED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {item.status === 'CONFIRMED' || item.status === 'RECEIVED' ? 'Liquidado' : 'Pendente'}
                      </span>
                    </td>
                    <td className="p-5 whitespace-nowrap">
                      {item.url_comprovante && (
                        <a href={item.url_comprovante} target="_blank" rel="noreferrer" className="text-[#1883FF] font-black no-underline text-xs flex items-center gap-1.5 transition-colors hover:text-slate-900">
                          RECIBO <ExternalLink size={14} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-[9999] p-4">
          <div className="relative w-full max-w-[440px] bg-white rounded-[40px] p-8 sm:p-12 border border-white/10 shadow-2xl animate-scale-in">
            <button className="absolute top-6 right-6 border-none bg-transparent cursor-pointer text-[#64748b] hover:text-[#1883FF] transition-colors" onClick={() => setShowModal(false)}>
              <X size={20} />
            </button>
            
            <div className="text-center mb-8">
               <ShieldCheck size={48} className={`mx-auto mb-4 ${planoSelecionado === 'COMUM' ? 'text-[#1883FF]' : 'text-[#6D28D9]'}`} />
               <h2 className="text-xl sm:text-2xl font-black uppercase italic tracking-tight text-slate-900 m-0">
                 Faturamento ({planoSelecionado} - {cicloSelecionado})
               </h2>
            </div>
            
            <form onSubmit={handleAssinar} className="flex flex-col gap-5">
              <div className="text-left">
                <label className="flex items-center gap-2 text-[10px] font-black uppercase text-[#64748b] mb-2.5 ml-1"><User size={14} /> CPF ou CNPJ do Titular</label>
                <input type="text" className="w-full p-4 bg-[#F8F7FF] border-2 border-purple-50 rounded-2xl text-sm font-bold text-slate-800 outline-none transition-all focus:border-[#6D28D9] focus:bg-white focus:ring-4 focus:ring-[#6D28D9]/10" placeholder="000.000.000-00" required value={formData.cpfCnpj} onChange={handleCpfChange} />
              </div>
              
              <div className="text-left">
                <label className="flex items-center gap-2 text-[10px] font-black uppercase text-[#64748b] mb-2.5 ml-1"><Smartphone size={14} /> Celular com DDD</label>
                <input type="text" className="w-full p-4 bg-[#F8F7FF] border-2 border-purple-50 rounded-2xl text-sm font-bold text-slate-800 outline-none transition-all focus:border-[#6D28D9] focus:bg-white focus:ring-4 focus:ring-[#6D28D9]/10" placeholder="85999999999" required value={formData.telefone} onChange={(e) => setFormData({...formData, telefone: e.target.value})} />
              </div>
              
              {!isReativando && (
                <div className="bg-[#F8F7FF] p-5 rounded-2xl border border-purple-50 text-left">
                  <div className="flex gap-3 items-start">
                    <AlertCircle size={20} className={`${planoSelecionado === 'COMUM' ? 'text-[#1883FF]' : 'text-[#6D28D9]'} shrink-0`} />
                    <p className="text-[11px] text-[#475569] font-bold m-0 leading-relaxed">
                      Você terá 7 dias grátis para testar. A primeira cobrança de R$ {fmt(cicloSelecionado === 'ANUAL' ? PRECOS[planoSelecionado].anualMes : PRECOS[planoSelecionado].mensal)} ocorrerá apenas após esse período.
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-amber-50 p-4 rounded-2xl border border-amber-200 text-left">
                <div className="flex gap-3 items-start">
                  <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800 font-medium m-0 leading-relaxed">
                    <strong className="font-black uppercase tracking-wider block mb-1">Confira seus dados:</strong> 
                    Certifique-se de que o número de celular está correto. É através de <b>SMS</b> e do seu <b>E-mail</b> de cadastro que você receberá os links de pagamento.
                  </p>
                </div>
              </div>
              
              <button 
                className={`w-full h-14 text-white border-none rounded-2xl text-sm font-black uppercase tracking-wider flex items-center justify-center gap-3 cursor-pointer transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 active:scale-98 disabled:opacity-50 shadow-lg ${planoSelecionado === 'COMUM' ? 'bg-slate-900 shadow-slate-900/20' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] shadow-[#6D28D9]/20'}`}
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : <CreditCard size={20} />}
                {loading ? 'Processando...' : 'Ativar Agora'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanosPage;